import { useCallback, useEffect, useRef, useState } from 'react'
import { API_URLS } from '@chessx/shared/api-routes'
import { INITIAL_FEN } from '@chessx/shared/chess-core'
import type { OpeningChildDto } from '@chessx/shared/opening-tree'
import axiosInstance from '../../network/axiosInstance'

export const ROOT_FEN = INITIAL_FEN

export const lineKey = (line: readonly string[]): string => line.join(' ')

const withAdded = (set: ReadonlySet<string>, value: string): ReadonlySet<string> =>
  new Set(set).add(value)

const withRemoved = (set: ReadonlySet<string>, value: string): ReadonlySet<string> => {
  const next = new Set(set)
  next.delete(value)
  return next
}

export const useOpeningTree = () => {
  const childrenCache = useRef(new Map<string, OpeningChildDto[]>())
  const [childrenByLine, setChildrenByLine] = useState<Record<string, OpeningChildDto[]>>({})
  const [rootFen, setRootFen] = useState(ROOT_FEN)
  const [rootLine, setRootLine] = useState<readonly string[]>([])
  const [expandedLines, setExpandedLines] = useState<ReadonlySet<string>>(new Set(['']))
  const [loadingLines, setLoadingLines] = useState<ReadonlySet<string>>(new Set())
  const [selectedFen, setSelectedFen] = useState<string | null>(null)
  const [selectedLine, setSelectedLine] = useState<readonly string[]>([])
  const [rootLoaded, setRootLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ensureChildren = useCallback(
    async (fen: string, line: readonly string[]): Promise<OpeningChildDto[]> => {
      const key = lineKey(line)
      const cached = childrenCache.current.get(key)
      if (cached) return cached

      setLoadingLines(prev => withAdded(prev, key))
      try {
        const response = await axiosInstance.get(API_URLS.OPENING_CHILDREN, {
          params: line.length > 0 ? { fen, line: key } : { fen }
        })
        const children = (response.data?.data?.children ?? []) as OpeningChildDto[]
        childrenCache.current.set(key, children)
        setChildrenByLine(prev => ({ ...prev, [key]: children }))
        setError(null)
        return children
      } catch {
        setError('Unable to load the Opening Tree.')
        return []
      } finally {
        setLoadingLines(prev => withRemoved(prev, key))
      }
    },
    []
  )

  useEffect(() => {
    setRootLoaded(false)
    ensureChildren(rootFen, rootLine).finally(() => setRootLoaded(true))
  }, [ensureChildren, rootFen, rootLine])

  const toggleLine = useCallback(
    async (fen: string, line: readonly string[]) => {
      const key = lineKey(line)
      if (expandedLines.has(key)) {
        setExpandedLines(prev => withRemoved(prev, key))
        return
      }
      setExpandedLines(prev => withAdded(prev, key))
      await ensureChildren(fen, line)
    },
    [expandedLines, ensureChildren]
  )

  const revealLine = useCallback(
    async (san: readonly string[]): Promise<void> => {
      setRootFen(ROOT_FEN)
      setRootLine([])
      const ancestors: string[] = []
      const walked: string[] = []
      let fen = ROOT_FEN

      for (const move of san) {
        const children = await ensureChildren(fen, walked)
        const match = children.find(child => child.san === move)
        if (!match) break
        ancestors.push(lineKey(walked))
        walked.push(move)
        fen = match.fen
      }

      setExpandedLines(prev => {
        const next = new Set(prev)
        ancestors.forEach(ancestor => next.add(ancestor))
        return next
      })
      setSelectedFen(fen)
      setSelectedLine(walked)
    },
    [ensureChildren]
  )

  const selectNode = useCallback((fen: string, line: readonly string[]) => {
    setSelectedFen(fen)
    setSelectedLine(line)
  }, [])

  const rerootAt = useCallback(
    async (fen: string, line: readonly string[] = []): Promise<void> => {
      await ensureChildren(fen, line)
      setRootFen(fen)
      setRootLine(line)
      setExpandedLines(prev => withAdded(prev, lineKey(line)))
      setSelectedFen(fen)
      setSelectedLine(line)
    },
    [ensureChildren]
  )

  const resetRoot = useCallback(() => {
    setRootFen(ROOT_FEN)
    setRootLine([])
    setSelectedFen(null)
    setSelectedLine([])
  }, [])

  const collapseAll = useCallback(() => {
    setExpandedLines(new Set())
    setSelectedFen(null)
    setSelectedLine([])
  }, [])

  return {
    childrenByLine,
    rootFen,
    rootLine,
    expandedLines,
    loadingLines,
    selectedFen,
    selectedLine,
    rootLoaded,
    error,
    loadChildren: ensureChildren,
    toggleLine,
    revealLine,
    rerootAt,
    resetRoot,
    collapseAll,
    selectNode
  }
}
